import { Helmet } from 'react-helmet-async';
import { useState, useCallback, type MouseEvent, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';

// @mui
import {
	Card,
	Table,
	Stack,
	Paper,
	Popover,
	TableRow,
	LinearProgress,
	MenuItem,
	TableBody,
	TableCell,
	Container,
	Typography,
	IconButton,
	TableContainer,
	TablePagination,
} from '@mui/material';
// components
import Iconify from '../../components/iconify';
import Scrollbar from '../../components/scrollbar';
import { ListHead, ListToolbar, type HeadCell } from '../../components/list';

import { useGetUsersQuery } from '../../slices/apis/userApi';
import type { UserProps, OrderByProps } from '../../models';
import { applySortFilter, getComparator, type Order } from '../../utils/tableSorter';

// ----------------------------------------------------------------------

const TABLE_HEAD: HeadCell[] = [
	{ id: 'id', label: 'ID', align: 'left' },
	{ id: 'firstname', label: 'First Name', align: 'left' },
	{ id: 'lastname', label: 'Last Name', align: 'left' },
	{ id: 'phone', label: 'Phone', align: 'left' },
	{ id: 'email', label: 'Email', align: 'left' },
	{ id: 'company', label: 'Company', align: 'left' },
	{ id: 'role', label: 'Role', align: 'left' },
	{ id: '', label: '', align: 'right' },
];

// ----------------------------------------------------------------------

export default function Users() {
	const [menuOpen, setMenuOpen] = useState<HTMLElement | null>(null);
	const [page, setPage] = useState<number>(0);
	const [order, setOrder] = useState<Order>('desc');
	const [selected] = useState<number[]>([]);
	const [orderBy, setOrderBy] = useState<keyof OrderByProps>('id');
	const [filterName, setFilterName] = useState('');
	const [rowsPerPage, setRowsPerPage] = useState(50);
	const [selectedUserId, setSelectedUserId] = useState<string | number | null>(null);
	const navigate = useNavigate();

	const { data, isLoading } = useGetUsersQuery();

	let emptyRows = 0;
	let filteredData: UserProps[] = [];
	let isNotFound = !filteredData.length && !!filterName;

	const handleOpenMenu = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
		setMenuOpen(e.currentTarget);
		setSelectedUserId(e.currentTarget.getAttribute('data-user-id'));
	}, []);

	const handleCloseMenu = () => {
		setMenuOpen(null);
	};

	const handleDataSort = (event: MouseEvent<unknown>, property: keyof OrderByProps) => {
		const isAsc = orderBy === property && order === 'asc';
		setOrder(isAsc ? 'desc' : 'asc');
		setOrderBy(property);
	};

	const handleChangePage = (event: MouseEvent<HTMLButtonElement>, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
		setPage(0);
		setRowsPerPage(Number.parseInt(event.target.value, 10));
	};

	const handleFilterByName = (event: ChangeEvent<HTMLInputElement>) => {
		setPage(0);
		setFilterName(event.target.value);
	};

	const handleEditUser = () => {
		navigate(`/dashboard/admin/users/${selectedUserId}/edit`);
		setMenuOpen(null);
	};

	if (isLoading) {
		return (
			<>
				<Helmet>
					<title>Users - {process.env.REACT_APP_ORG}</title>
				</Helmet>
				<Container sx={{ width: '75%' }}>
					<Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
						<Typography variant="h4" gutterBottom>
							Users
						</Typography>
					</Stack>
					<LinearProgress />
				</Container>
			</>
		);
	}

	if (data) {
		emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - data.length) : 0;
		filteredData = applySortFilter({
			list: data,
			comparator: getComparator(order, orderBy),
			query: filterName,
			orderBy: 'firstname',
		});
		isNotFound = !filteredData.length && !!filterName;
	}

	return (
		<>
			<Helmet>
				<title>Users - {process.env.REACT_APP_ORG}</title>
			</Helmet>
			<Container sx={{ width: '75%' }}>
				<Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
					<Typography variant="landing_page_heading" gutterBottom>
						Users
					</Typography>
				</Stack>

				<Card>
					<ListToolbar
						numSelected={selected.length}
						filterName={filterName}
						onFilterName={handleFilterByName}
						placeHolderText={'User firstname...'}
					/>

					<Scrollbar>
						<TableContainer>
							<Table>
								<ListHead order={order} orderBy={orderBy} headLabel={TABLE_HEAD} onRequestSort={handleDataSort} />
								<TableBody>
									{filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
										const { id, firstname, lastname, email, phone, company, roles } = row;

										return (
											<TableRow hover key={id} tabIndex={-1} selected={selected.indexOf(id) !== -1}>
												<TableCell align="left">
													<Typography variant="subtitle2">{id}</Typography>
												</TableCell>
												<TableCell align="left">{firstname}</TableCell>
												<TableCell align="left">{lastname}</TableCell>
												<TableCell align="left">{phone}</TableCell>
												<TableCell align="left">{email}</TableCell>
												<TableCell align="left">{company}</TableCell>
												<TableCell align="left">{roles?.[0]?.role}</TableCell>
												<TableCell align="right">
													<IconButton size="large" color="inherit" data-user-id={id} onClick={handleOpenMenu}>
														<Iconify icon={'eva:more-vertical-fill'} />
													</IconButton>
												</TableCell>
											</TableRow>
										);
									})}
									{emptyRows > 0 && (
										<TableRow style={{ height: 53 * emptyRows }}>
											<TableCell colSpan={6} />
										</TableRow>
									)}
								</TableBody>

								{isNotFound && (
									<TableBody>
										<TableRow>
											<TableCell align="center" colSpan={6} sx={{ py: 3 }}>
												<Paper
													sx={{
														textAlign: 'center',
													}}
												>
													<Typography variant="h6" paragraph>
														Not found
													</Typography>

													<Typography variant="body2">
														No results found for &nbsp;
														<strong>&quot;{filterName}&quot;</strong>.
														<br /> Check your typos or use complete words
													</Typography>
												</Paper>
											</TableCell>
										</TableRow>
									</TableBody>
								)}
							</Table>
						</TableContainer>
					</Scrollbar>

					<TablePagination
						rowsPerPageOptions={[25, 50, 75]}
						component="div"
						count={data ? data.length : 0}
						rowsPerPage={rowsPerPage}
						page={page}
						onPageChange={handleChangePage}
						onRowsPerPageChange={handleChangeRowsPerPage}
					/>
				</Card>
			</Container>

			<Popover
				open={Boolean(menuOpen)}
				anchorEl={menuOpen}
				onClose={handleCloseMenu}
				anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
				PaperProps={{
					sx: {
						p: 1,
						width: 140,
						'& .MuiMenuItem-root': {
							px: 1,
							typography: 'body2',
							borderRadius: 0.75,
						},
					},
				}}
			>
				<MenuItem onClick={handleEditUser}>
					<Iconify icon={'eva:edit-fill'} sx={{ mr: 1 }} />
					View/Edit
				</MenuItem>
			</Popover>
		</>
	);
}
